import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { withLocalize } from 'react-localize-redux';
import Icon, { IconSizes } from '../../components/foundations/Icon';
import { buildArtifactDocument } from '../../artifactPage/htmlShell';
import { artifactPagePath } from '../../artifactPage/artifactPageUrl';
import { CHAT_EVENTS, trackChatEvent } from '../constants';
import { artifactEventParams } from '../htmlArtifacts';
import css from './ArtifactActions.css';

const TRANSLATION_PREFIX = 'STATIC.PAGES.ANALYSIS_CHAT.ARTIFACT_SHELF';
const MAX_FILE_NAME_LENGTH = 80;

const toFileName = title =>
    `${title
        .replace(/[\\/:*?"<>|\r\n\t]/g, ' ')
        .trim()
        .slice(0, MAX_FILE_NAME_LENGTH) || 'artifact'}.html`;

// Everything you can do to the document the shelf has open. The download builds
// its own file here rather than taking a handler, so the shelf is left with
// fetching and layout.
const ArtifactActions = ({ artifact = null, content = null, isMaximized, onToggleMaximized, onClose, translate }) => {
    const eventParams = useMemo(() => artifactEventParams(artifact?.contentUrl), [artifact]);

    const handleToggleMaximized = () => {
        trackChatEvent(CHAT_EVENTS.TOGGLE_HTML_ARTIFACT_MAXIMIZED, { ...eventParams, isMaximized: !isMaximized });
        onToggleMaximized();
    };

    const handleOpenInNewTab = () => trackChatEvent(CHAT_EVENTS.OPEN_HTML_ARTIFACT_NEW_TAB, eventParams);

    const handleClose = () => {
        trackChatEvent(CHAT_EVENTS.CLOSE_HTML_ARTIFACT, eventParams);
        onClose();
    };

    const handleDownload = useCallback(() => {
        if (!content) return;
        trackChatEvent(CHAT_EVENTS.DOWNLOAD_HTML_ARTIFACT, eventParams);
        // The shell travels with the fragment, so the saved file opens standalone
        // under the same CSP the shelf renders it with.
        const url = URL.createObjectURL(new Blob([buildArtifactDocument(content)], { type: 'text/html' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = toFileName(artifact?.title || translate(`${TRANSLATION_PREFIX}.TITLE_FALLBACK`));
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }, [content, artifact, translate, eventParams]);

    return (
        <div className={css.actions}>
            <Icon
                name={isMaximized ? 'arrows-diagonal-minimize-2' : 'arrows-diagonal'}
                size={IconSizes.SMALL}
                onClick={handleToggleMaximized}
                hoverText={`${TRANSLATION_PREFIX}.${isMaximized ? 'MINIMIZE' : 'MAXIMIZE'}`}
            />
            <a
                className={css.actionLink}
                href={artifactPagePath(artifact?.contentUrl)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleOpenInNewTab}
            >
                <Icon name="external-link" size={IconSizes.SMALL} hoverText={`${TRANSLATION_PREFIX}.NEW_TAB`} />
            </a>
            <Icon
                name="file-download"
                size={IconSizes.SMALL}
                disabled={!content}
                onClick={handleDownload}
                hoverText={`${TRANSLATION_PREFIX}.DOWNLOAD`}
            />
            <Icon name="x" size={IconSizes.X_SMALL} onClick={handleClose} hoverText={`${TRANSLATION_PREFIX}.CLOSE`} />
        </div>
    );
};

ArtifactActions.propTypes = {
    artifact: PropTypes.shape({
        title: PropTypes.string,
        contentUrl: PropTypes.string,
    }),
    content: PropTypes.string,
    isMaximized: PropTypes.bool,
    onToggleMaximized: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withLocalize(ArtifactActions);
